import { WavRecorder } from 'wavtools';

/**
 * `WavRecorder` with explicit `getUserMedia` constraints.
 *
 * Upstream's `begin()` calls `getUserMedia({ audio: true })`
 * (wav_recorder.js:315), which leaves `echoCancellation`,
 * `noiseSuppression`, and `autoGainControl` to browser defaults.
 * Most browsers default these to `true` for the unspecified case,
 * but it's not specified by the standard - Safari has historically
 * been inconsistent and Chrome's default has flipped between
 * versions. Without explicit constraints, AEC may not engage and
 * the AI's voice bleeds back into the mic, tripping server VAD and
 * truncating AI audio in the recording.
 *
 * Rather than fork the parent's `begin()` (which also sets up the
 * AudioWorklet, port message handlers, analyser node - easy to get
 * subtly wrong), we patch `navigator.mediaDevices.getUserMedia` for
 * the single super-call and restore it afterwards.
 */
export class TunedWavRecorder extends WavRecorder {
  override async begin(deviceId?: string): Promise<true> {
    const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
      navigator.mediaDevices
    );

    const desired: MediaTrackConstraints = {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    if (deviceId) {
      desired.deviceId = { exact: deviceId };
    }

    navigator.mediaDevices.getUserMedia = async (
      cfg?: MediaStreamConstraints
    ) => {
      // Merge our voice-friendly constraints onto whatever the
      // parent passed. We can't know if a future wavtools version
      // adds its own audio constraints, so we layer ours on top.
      const audio =
        typeof cfg?.audio === 'object' ? { ...cfg.audio, ...desired } : desired;
      return originalGetUserMedia({ ...cfg, audio });
    };

    try {
      return await super.begin(deviceId);
    } finally {
      navigator.mediaDevices.getUserMedia = originalGetUserMedia;
    }
  }
}
